import styled from 'styled-components';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import ExtendButton from '../../../components/ExtendButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { DatePicker, Empty, Select, Table, TableColumnsType } from 'antd';
import Button from '../../../components/Button';
import config from '../../../lib/config';
import Pagination from '../../../components/Pagination';
import {
  useGetSemestersQuery,
  useLazyGetOverlappingSemestersQuery,
  useSaveOverlappingSemestersMutation,
  useDeleteOverlappingSemesterMutation
} from '../../../api/program/overlapping-semester';
import { ISemester } from '../../../models/semesters.model';
import { getCommonCodeLabel } from '../../../lib/store';
import { infoVariant, successVariant } from '../../../lib/barVariant';
import moment from 'moment/moment';
import { getKey } from '../../../lib/util';
import Split from 'react-split';
import TableEmpty from '../../../components/common/TableEmpty';
import _ from 'lodash';

const Block = styled.div`
  .close-left {
    transform: translate(-25%);
  }

  .label-wrapper {
    width: 110px;
  }

  .table-inside {
    width: 100%;

    th {
      background: ghostwhite;
    }
  }

  .table-basic th,
  td {
    vertical-align: middle;
  }

  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
`;
const OverlappingSemester = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isExtended, setIsExtended] = useState(false);
  const [year, setYear] = useState('');
  const [page, setPage] = useState(1);
  const [currentOverlapInformation, setCurrentOverlapInformation] = useState<any>(null);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);

  const SY0005 = commonCodes.find(
    (item) => item.commonCode === 'SY0005',
  )?.detailCode;
  const _SY0005 = _.orderBy(SY0005, ['orderSeq'], ['desc']);

  const PR0005 = commonCodes.find(
    (item) => item.commonCode === 'PR0005',
  )?.detailCode;

  const [filteredSchedule, setFilteredSchedule] = useState<any>(null);
  const { data: semesterResponse, isLoading: isSemestersLoading } = useGetSemestersQuery(year ? { year } : null);
  const [overlappingTrigger, {data: overlappingResponse}] = useLazyGetOverlappingSemestersQuery();
  const [overlappingSaveTrigger, { isSuccess: isSaveSuccess, isLoading: isSaveLoading }] = useSaveOverlappingSemestersMutation();
  const [overlappingDeleteTrigger, { isSuccess: isDeleteSuccess, isLoading: isDeleteLoading }] = useDeleteOverlappingSemesterMutation();
  const semesters = semesterResponse?.data.filter((item:any)=>item.semesterType==='RG');
  const semestersSchedule = overlappingResponse?.data;

  const MODE = {
    READ: 'read',
    WRITE: 'write'
  }

  const VALIDATE_CHECK_LIST = [
    {id: 'overlapTypeCode', name: t('program.overlappingSemester.1')},
    {id: 'srtDate', name: t('program.overlappingSemester.2')},
    {id: 'endDate', name: t('program.overlappingSemester.3')},
  ]

  useEffect(() => {
    if (!semestersSchedule) return;

    setFilteredSchedule(semestersSchedule.map((record: any) => {
      return {
        id: getKey(),
        mode: MODE.READ,
        ...record,
      }
    }));
  }, [semestersSchedule])

  const columns: TableColumnsType<ISemester> = [
    {
      title: t('common.column.semester'),
      dataIndex: 'semesterName',
      key: 'semesterName',
      sorter: (a, b) => a.semesterName?.localeCompare(b.semesterName),
      align: 'center'
    },
    {
      title: t('연도'),
      dataIndex: 'learningYearCode',
      key: 'learningYearCode',
      sorter: (a, b) => a.learningYearCode?.localeCompare(b.learningYearCode),
      align: 'center',
    },
    {
      title: t('program.semester.column33'),
      dataIndex: 'semesterType',
      key: 'semesterType',
      align: 'center',
      render: (value: string) => getCommonCodeLabel('PR0002', value, i18n.language),
    },
  ];

  const MAX_SEMESTER = 4;
  const MIN_SEMESTER = 1;

  const SEMESTER_TYPE = {
    PREVIEW: (value: number) => (value - 1) < MIN_SEMESTER ? MAX_SEMESTER : value - 1,
    REVIEW: (value: number) => (value + 1) > MAX_SEMESTER ? MIN_SEMESTER : value + 1
  }

  const LEARNING_YEAR = {
    PREVIEW: (value: number, year: number) => (value - 1) < MIN_SEMESTER ? year - 1 : year,
    REVIEW: (value: number, year: number) => (value + 1) > MAX_SEMESTER ? year + 1 : year
  }

  const SEMESTER_DIV_TYPES: any = {
    PP: SEMESTER_TYPE.PREVIEW,
    RV: SEMESTER_TYPE.REVIEW
  }

  const SEMESTER_YEAR_TYPES: any = {
    PP: LEARNING_YEAR.PREVIEW,
    RV: LEARNING_YEAR.REVIEW
  }
  const changeSemesterDiv = (typeCode: string, semesterDiv: string) => {
    return `0${SEMESTER_DIV_TYPES[typeCode](Number(semesterDiv))}`;
  }

  const changeSemesterYear = (typeCode: string, semesterDiv: string, year: number) => {
    return SEMESTER_YEAR_TYPES[typeCode](Number(semesterDiv), Number(year));
  }

  const semesterColumns: TableColumnsType<any> = [
    {
      title: t('program.overlappingSemester.4'),
      dataIndex: 'overlapTypeCode',
      align: 'center',
      render: (value, record) => {
        return record?.mode === MODE.READ
          ? getCommonCodeLabel('PR0005', value, i18n.language)
          : (
            <Select
              className="w-100"
              onChange={(value) => {
                setFilteredSchedule(filteredSchedule?.map((element:any) => {
                  if (element?.id === record.id) {
                    return {
                      ...element,
                      overlapTypeCode: value,
                      semesterDiv: changeSemesterDiv(value, element.overlapSemesterDiv),
                      learningYearCode: changeSemesterYear(value, element.overlapSemesterDiv, element.overlapYearCode),
                    }
                  }
                  return element;
                }))
              }}
              value={value}
            >
              {
                PR0005?.map((element) => {
                  return <Select.Option
                    key={element.detailCode}
                    value={element.detailCode}
                    disabled={filteredSchedule?.find((_element: any) => _element.overlapTypeCode === element.detailCode)}>
                    {i18n.language === 'ko' ? element.korName : element.engName}
                  </Select.Option>
                })
              }

            </Select>
          );
      },
    },
    {
      title: t('program.overlappingSemester.2'),
      dataIndex: 'srtDate',
      align: 'center',
      render: (value, record) => {
        return record?.mode === MODE.READ
          ? moment(value).format('YYYY-MM-DD')
          : <div className="date-picker__area">
              <DatePicker
                  allowClear={false}
                  onChange={(value) => {
                    setFilteredSchedule(filteredSchedule?.map((element:any) => {
                      if (element?.id === record.id) {
                        return {
                          ...element,
                          srtDate: moment(value).format("YYYYMMDD")
                        }
                      }
                      return element;
                    }))
                  }}
                  value={moment(value)}
              />
          </div>
      }
    },
    {
      title: t('program.overlappingSemester.3'),
      dataIndex: 'endDate',
      align: 'center',
      render: (value, record) => {
        return record?.mode === MODE.READ
          ? moment(value).format('YYYY-MM-DD')
          : <div className="date-picker__area">
            <DatePicker
              allowClear={false}
              onChange={(value) => {
                setFilteredSchedule(filteredSchedule?.map((element:any) => {
                  if (element?.id === record.id) {
                    return {
                      ...element,
                      endDate: moment(value).format("YYYYMMDD")
                    }
                  }
                  return element;
                }))
              }}
              value={moment(value)}
            />
          </div>
      }
    },
    {
      title: t('program.overlappingSemester.5'),
      dataIndex: 'firstMemberName',
      align: 'center',
      render: (value, record) => value ? (i18n.language === 'ko' ? value : record.firstMemberEngName) : '-'
    },
    {
      title: t('program.overlappingSemester.6'),
      dataIndex: 'firstRegDttm',
      align: 'center',
      render: (value) => value ? moment(value).format('YYYY-MM-DD') : '-'
    },
    {
      title: t('program.overlappingSemester.7'),
      dataIndex: 'lastMemberName',
      align: 'center',
      render: (value, record) => value ? (i18n.language === 'ko' ? value : record.lastMemberEngName) : '-'
    },
    {
      title: t('program.overlappingSemester.8'),
      dataIndex: 'lastUpdDttm',
      align: 'center',
      render: (value) => value ? moment(value).format('YYYY-MM-DD') : '-'
    },
    {
      title: t('program.overlappingSemester.9'),
      align: 'center',
      render: (value, record) => (
        record?.mode === MODE.READ ? (
          <div className="comm-btn__area justify-content-center">
            <Button  className="btn-basic small pink-line"
                     onClick={() => setFilteredSchedule(filteredSchedule?.map((item: any) => {
                       if (item.id === record.id) {
                         return {
                           ...item,
                           mode: MODE.WRITE
                         }
                       }
                       return item;
                     }))}
                     text={t('common.button.edit')} />
            <Button  className="btn-basic small gray"
                     onClick={() => overlappingDeleteTrigger({ ...record })}
                     text={t('common.button.delete')} />
          </div>
        ) : '-'
      )
    }
  ];

  const isAllRegisters = (element: { overlapTypeCode: string; }) => PR0005?.map(_element => _element.detailCode).includes(element.overlapTypeCode);
  const handleAddSchedule = () => {
    if (PR0005 && filteredSchedule?.length >= PR0005.length) {
      enqueueSnackbar(t('program.overlappingSemester.10'), infoVariant);
      return
    }

    if (filteredSchedule && filteredSchedule.filter(isAllRegisters).length === PR0005?.length) {
      enqueueSnackbar(t('program.overlappingSemester.11'), infoVariant);
      return;
    }

    const inputForm = {
      id: getKey(),
      mode: MODE.WRITE,
      overlapTypeCode: '',
      useYn: 'Y',
      srtDate: moment().format('YYYYMMDD'),
      endDate: moment().format('YYYYMMDD'),
      ...currentOverlapInformation
    }

    setFilteredSchedule(filteredSchedule ? [...filteredSchedule, inputForm] : [inputForm])
  }

  const handleSaveSchedule = () => {
    if (!filteredSchedule?.find((item: any) => item?.id)) {
      enqueueSnackbar(t('program.overlappingSemester.12'), infoVariant);
      return;
    }

    const checked =
      filteredSchedule.filter((item: any) => VALIDATE_CHECK_LIST.find(valid => !item[valid.id]));

    if (checked.length > 0) {
      enqueueSnackbar(`[${VALIDATE_CHECK_LIST.map((item: any) => item.name).join(',')}]${t('program.overlappingSemester.13')}`, infoVariant);
      return;
    }

    overlappingSaveTrigger(filteredSchedule);
  }

  useEffect(() => {
    if (isSaveSuccess && !isSaveLoading) {
      enqueueSnackbar(t('common.message.save1'), successVariant);
    }
  }, [isSaveSuccess, isSaveLoading])

  useEffect(() => {
    if (isDeleteSuccess && !isDeleteLoading) {
      enqueueSnackbar(t('common.message.delete'), successVariant);
    }
  }, [isDeleteSuccess, isDeleteLoading])

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>
              {t('program.semester.column9')}
            </label>
            <Select
              className="w-120p"
              value={year}
              onChange={value => setYear(value)}
            >
              <Select.Option key={'all'} value={''}>
                {t('common.text.all')}
              </Select.Option>
              {
                _SY0005!.map(({korName: label, detailCode: value}) => {
                  return (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  )
                })
              }
            </Select>
          </li>
        </ul>
      </div>
    </div>
    <Block className="content">
      <SimpleHeader
        parentMenu={t('common.menu.program')}
        currentMenu={t('program.overlappingSemester.14')}
        iconFile="icon-program-big.svg"
        onlyTitle={true}
      />

      <Split 
        className='splite-divide'
        sizes={[30, 70]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >
        <div className='col-frame left_panel'>
          <div className="comm-bx__between">
            <div className="comm-sch_result">{t('common.text.count')}: <b>{semesters?.length ?? 0}</b>{t('common.text.unit')}</div>
          </div>
          <div>
            <Table
              className='comm-table__area mt-0'
              columns={columns}
              dataSource={semesters ?? []}
              pagination={{
                current: page,
                pageSize: config.middlePageSize
              }}
              loading={isSemestersLoading}
              onRow={(record: any) => {
                return {
                  onClick: () => {
                    setCurrentOverlapInformation({
                      overlapYearCode: record.learningYearCode,
                      overlapSemesterDiv: record.semesterDiv
                    })
                    overlappingTrigger({ year: record.learningYearCode, semesterDiv: record.semesterDiv });
                  }
                };
              }}
              rowClassName={(record: any) => (
                record.learningYearCode === currentOverlapInformation?.overlapYearCode
                && record.semesterDiv === currentOverlapInformation?.overlapSemesterDiv
              ) ? 'chosen' : 'pointer'}
              rowKey="semesterCode"
            />
            <div className="comm-bx__between">
              <div className="common-paging right">
                <Pagination
                  count={semesters?.length ?? 0}
                  page={page}
                  perPage={config.middlePageSize}
                  setPage={setPage}
                  small
                />
              </div>
            </div>
          </div>
        </div>
        <div className='col-frame right_panel'>
          <div className='comm-bx__between'>
            <h3 className='sub-title'>{t('program.overlappingSemester.15')}</h3>                
          </div>
          {semestersSchedule ?
            <Table
              className='comm-table__area'
              columns={semesterColumns}
              dataSource={filteredSchedule}
              rowKey="lessonPlanCode"
            />
            : <div className='comm-table__area'><TableEmpty text={t('shuttle.text.40')} /></div>
          }
          {
              currentOverlapInformation && (
                <div className="comm-bx__space">
                  <div className="comm-btn__area right">
                    <Button
                      className="btn-basic pink-line"
                      onClick={handleAddSchedule}
                      text={t('program.overlappingSemester.16')}
                    />
                    <Button
                      className="btn-basic pink"
                      onClick={handleSaveSchedule}
                      text={t('common.button.save')}
                    />
                  </div>
                </div>
              )
            }
        </div>
      </Split>
    </Block>
    </>
  )
}

export default OverlappingSemester
